import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { parseWallClockTime } from '../../../shared/Helpers/timezones';
import API from '../../../redux/API/Main/API';

import { CustomButton, Text } from '../../../shared/uiComponents';
import { Loader } from '../../../components/StyledComponents';
import { PRIMARY } from '../../../shared/Helpers/colors';
import { DASH_COLORS } from '../helpers';
import { errorNormalizer, getClientDisplayLabel, getInitials } from '../../../shared/Helpers/functions';
import {
  SummaryCheckCircleSvg,
  SummaryClockCircleSvg,
  SummaryBarChartSvg,
  CalendarOutlineSvg,
} from '../../../shared/Assets/svgIcons';
import { Calendar } from '../../../shared/Assets/menuIcons';
import PendingApproval from './PendingApproval';
import AuthorizationDetailPopup from './AuthorizationDetailPopup';
import { getSessionButtonProps } from '../helpers';

import { DispatchProperties, useSelector } from '../../../redux/store';
import {
  getBcbaDashboard,
  BcbaDashboardClient,
  BcbaDashboardSummary,
  BcbaDashboardTarget,
  BcbaDashboardGoalProgress,
} from '../../../redux/State/clientSlice/dashboardSlice';
import { startEventSession } from '../../../redux/State/clientSlice/sessionSlice';

const formatTime = (isoString: string) => {
  const date = parseWallClockTime(isoString).toDate();
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' });
};

interface SummaryCardDef {
  label: string;
  value: number;
  subtitle: string;
  linkText?: string;
  icon: React.ReactNode;
  iconClass: string;
  accentClass: string;
  onClick?: () => void;
}

const BcbaSummaryCards = ({
  summary,
  onPendingSignOffClick,
}: {
  summary: BcbaDashboardSummary | null;
  onPendingSignOffClick: () => void;
}) => {
  const navigate = useNavigate();
  const cards: SummaryCardDef[] = [
    {
      label: 'Total Clients',
      value: summary?.totalClients ?? 0,
      subtitle: 'Active caseload',
      icon: <SummaryCheckCircleSvg />,
      iconClass: 'btSummaryIconGreen',
      accentClass: 'btSummaryCardAccentTeal',
      onClick: () => navigate('/clients'),
    },
    {
      label: 'Needs Attention',
      value: summary?.needsAttention ?? 0,
      subtitle: 'Declining or stalled targets',
      icon: <SummaryBarChartSvg />,
      iconClass: 'btSummaryIconOrange',
      accentClass: 'btSummaryCardAccentOrange',
    },
    {
      label: 'Notes Pending Sign-off',
      value: summary?.notesPendingSignOff ?? 0,
      subtitle: '',
      linkText: 'Review & bulk sign \u2192',
      icon: <SummaryClockCircleSvg />,
      iconClass: 'btSummaryIconPrimary',
      accentClass: 'btSummaryCardAccentGreen',
      onClick: (summary?.notesPendingSignOff ?? 0) > 0 ? onPendingSignOffClick : undefined,
    },
  ];

  return (
    <div className="btSummaryCards">
      {cards.map((card, index) => (
        <div
          className={`btSummaryCard ${card.accentClass} ${card.onClick ? 'btSummaryCardClickable' : ''}`}
          key={index}
          onClick={card.onClick}
          role={card.onClick ? 'button' : undefined}
          tabIndex={card.onClick ? 0 : undefined}
          onKeyDown={card.onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') card.onClick!(); } : undefined}
        >
          <div className="btSummaryCardHeader">
            <div className={`btSummaryIcon ${card.iconClass}`}>{card.icon}</div>
            <Text className="btSummaryValue" title={String(card.value)} type="h2" font="header" fontWeight="semiBold" />
          </div>
          <Text className="btSummaryLabel" title={card.label} type="psm" fontWeight="medium" />
          {card.linkText && card.onClick ? (
            <span className="bcbaSummaryLink">{card.linkText}</span>
          ) : (
            <Text className="btSummarySubtitle" title={card.subtitle} type="pxs" />
          )}
        </div>
      ))}
    </div>
  );
};

const ReassessmentAlert = ({ count }: { count: number }) => {
  const navigate = useNavigate();

  if (count === 0) return null;

  return (
    <div className="bcbaReassessmentAlert">
      <div className="bcbaReassessmentAlertContent">
        <span className="bcbaReassessmentAlertIcon">&#128203;</span>
        <div className="bcbaReassessmentAlertText">
          <span className="bcbaReassessmentAlertTitle">Upcoming Reassessments</span>
          <span className="bcbaReassessmentAlertSub">
            {count} client{count !== 1 ? 's are' : ' is'} due for 6-month reassessments this month &bull; Schedule assessment sessions
          </span>
        </div>
      </div>
      <div className="bcbaReassessmentAlertAction">
        <CustomButton
          title="View Calendar"
          sizeType="sm"
          onClick={() => navigate('/calendar')}
          style={{ background: DASH_COLORS.orange, border: 'none', color: 'white' }}
        />
      </div>
    </div>
  );
};

const getSessionDisplay = (sessionTypeName: string | null): { label: string; billingCode: string | null } => {
  if (!sessionTypeName) return { label: '', billingCode: null };
  const parts = sessionTypeName.split(' - ');
  const billingCode = parts[0]?.match(/^\d+/) ? parts[0].trim() : null;
  const label = parts.length > 1 ? parts.slice(1).join(' - ').trim() : sessionTypeName;
  return { label, billingCode };
};

const getFirstName = (fullName: string | null): string | null => {
  if (!fullName) return null;
  return fullName.split(' ')[0];
};

const TARGET_STATUS_CLASSES: Record<number, string> = {
  0: 'status-baseline',
  1: 'status-treatment',
  2: 'status-mastered',
  3: 'status-hold',
};

const TREND_STATUS_CLASSES: Record<string, string> = {
  declining: 'status-declining',
  stalled: 'status-declining',
  improving: 'status-treatment',
  nearingMastery: 'status-mastery',
  inTreatment: 'status-treatment',
  mastered: 'status-mastered',
  ready: 'status-baseline',
  onHold: 'status-hold',
};

const TargetStatusBadge = ({ target }: { target: BcbaDashboardTarget }) => (
  <span className={`bcbaTargetStatus ${target.trendStatus ? TREND_STATUS_CLASSES[target.trendStatus] : TARGET_STATUS_CLASSES[target.status.id] ?? ''}`}>
    {target.trendStatusLabel ?? target.status.name}
  </span>
);

const BcbaClientCard = ({ client }: { client: BcbaDashboardClient }) => {
  const dispatch = useDispatch<DispatchProperties>();
  const navigate = useNavigate();
  const [goalsExpanded, setGoalsExpanded] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [loadedGoals, setLoadedGoals] = useState<BcbaDashboardGoalProgress[] | null>(null);

  const clientLabel = getClientDisplayLabel(client.client, { shorten: true });

  const { clientStatus, clientStatusType } = (() => {
    if (client.activeTargetCount === 0) return { clientStatus: 'No Active Targets', clientStatusType: 'noTargets' };
    if (client.decliningTargets > 0) return { clientStatus: `${client.decliningTargets} target${client.decliningTargets !== 1 ? 's' : ''} declining`, clientStatusType: 'declining' };
    if (client.nearingMasteryTargets > 0) return { clientStatus: `${client.nearingMasteryTargets} target${client.nearingMasteryTargets !== 1 ? 's' : ''} nearing mastery`, clientStatusType: 'nearingMastery' };
    if (client.improvingTargets > 0) return { clientStatus: `${client.improvingTargets} target${client.improvingTargets !== 1 ? 's' : ''} improving`, clientStatusType: 'improving' };
    return { clientStatus: null, clientStatusType: null };
  })();

  const isExpiringSoon = client.authorizationExpiry
    ? dayjs(client.authorizationExpiry).diff(dayjs(), 'day') <= 30
    : false;

  const authExpiryLabel = client.authorizationExpiry
    ? isExpiringSoon
      ? `Reassessment due ${dayjs(client.authorizationExpiry).format('MMM D')}`
      : `Authorization expires ${dayjs(client.authorizationExpiry).format('MM/DD/YY')}`
    : null;

  const billingWeekRange = client.weeklyBillingInsuranceName && client.billingWeekStart && client.billingWeekEnd
    ? `${dayjs(client.billingWeekStart).format('ddd')} \u2013 ${dayjs(client.billingWeekEnd).format('ddd')}`
    : null;

  const sessionDisplay = client.nextSession
    ? getSessionDisplay(client.nextSession.sessionTypeName)
    : null;

  const canSkipSession = true;

  const handleStartSession = () => {
    if (!client.nextSession) return;
    if (client.nextSession.sessionId) {
      navigate(`/calendar/session/${client.nextSession.sessionId}/${client.client.id}`);
      return;
    }
    setSessionLoading(true);
    dispatch(startEventSession(client.nextSession.eventId))
      .then(unwrapResult)
      .then((response) => {
        if (!response.id || !response.client?.id) return;
        navigate(`/calendar/session/${response.id}/${response.client.id}`);
      })
      .catch(errorNormalizer)
      .finally(() => setSessionLoading(false));
  };

  const handleToggleGoals = async () => {
    if (!goalsExpanded && !loadedGoals && client.goalsCount > 0) {
      setGoalsLoading(true);
      try {
        const response = await API.ClientAPI.Dashboard.getBcbaClientGoals(client.client.id);
        if (!response.error) {
          setLoadedGoals(response as BcbaDashboardGoalProgress[]);
        }
      } catch (error) {
        console.error('Failed to load goals:', error);
      } finally {
        setGoalsLoading(false);
      }
    }
    setGoalsExpanded((prev) => !prev);
  };

  return (
    <div className="bcbaClientCard">
      <div className="bcbaClientCardHeader">
        <div className="bcbaClientCardLeft">
          <div className="btClientAvatar">{getInitials(client.client.fullName)}</div>
          <div className="bcbaClientCardInfo">
            <Text
              className="btClientName"
              title={clientLabel}
              type="psm"
              fontWeight="semiBold"
            />
            <div className="bcbaClientCardMeta">
              <span className="btSessionCardMetaItem">
                {client.activeTargetCount} active target{client.activeTargetCount !== 1 ? 's' : ''}
              </span>
              {authExpiryLabel && (
                <>
                <span className="bcbaMetaSeparator">&bull;</span>
                <AuthorizationDetailPopup
                  label={authExpiryLabel}
                  clientId={client.client.id}
                  isExpiringSoon={isExpiringSoon}
                />
                </>
              )}
            </div>
            {billingWeekRange && (
              <div className="bcbaBillingBadge">
                <span className="bcbaBillingBadgeLabel">{client.weeklyBillingInsuranceName} week:</span>
                {' '}{billingWeekRange}
              </div>
            )}
          </div>
        </div>
        {clientStatus && (
          <div className="bcbaClientStatusIndicator">
            <span className={`btStatusBadge ${clientStatusType === 'declining' ? 'bcbaStatusDeclining' : clientStatusType === 'nearingMastery' ? 'bcbaStatusNearingMastery' : clientStatusType === 'improving' ? 'bcbaStatusImproving' : 'upcoming'}`}>
              {clientStatus}
            </span>
          </div>
        )}
      </div>

      {client.nextSession && (
        <div className="bcbaClientCardSession">
          <CalendarOutlineSvg />
          <span className="bcbaClientCardSessionTime">
            <strong>{sessionDisplay?.label}:</strong>
            {' '}{dayjs(client.nextSession.date).format('dddd')} {formatTime(client.nextSession.startTime)}
            {client.nextSession.btName ? (
              <span className="bcbaClientCardSessionBt">
                {client.nextSession.sessionTypeName?.toLowerCase().includes('supervision')
                  ? ` (supervising BT ${getFirstName(client.nextSession.btName)})`
                  : ` with ${getFirstName(client.nextSession.btName)}`}
              </span>
            ) : (
              sessionDisplay?.billingCode ? ` (${sessionDisplay.billingCode})` : null
            )}
          </span>
        </div>
      )}

      {client.goalsCount > 0 && (
        <>
          <div
            className="bcbaGoalsToggle"
            onClick={handleToggleGoals}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleToggleGoals(); }}
          >
            <svg
              className={`bcbaToggleIcon ${goalsExpanded ? 'expanded' : ''}`}
              width="16" height="16" fill="currentColor" viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
            <span>{goalsLoading ? 'Loading...' : goalsExpanded ? 'Hide' : 'Show'} Goals Progress</span>
          </div>
          {goalsExpanded && loadedGoals && (
            <div className="bcbaGoalsDetail">
              {Object.entries(
                loadedGoals.reduce<Record<string, BcbaDashboardTarget[]>>((acc, goal) => {
                  const area = goal.skillAreaName || goal.programName;
                  if (!acc[area]) acc[area] = [];
                  acc[area].push(...goal.targets);
                  return acc;
                }, {})
              ).map(([areaName, targets]) => (
                <div key={areaName} className="bcbaGoalsProgram">
                  <div className="bcbaProgramName">{areaName}</div>
                  <div className="bcbaProgramTargets">
                    {targets.map((target) => (
                      <div key={target.id} className="bcbaTargetItem">
                        <span className="bcbaTargetName">{target.name}</span>
                        <TargetStatusBadge target={target} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div className="btSessionCardAction">
        {client.nextSession && (
          <CustomButton
            title={getSessionButtonProps({ isCompleted: client.nextSession.isCompleted, sessionId: client.nextSession.sessionId, canSkipSession }).title}
            sizeType="sm"
            onClick={handleStartSession}
            loading={sessionLoading}
            disabled={getSessionButtonProps({ isCompleted: client.nextSession.isCompleted, sessionId: client.nextSession.sessionId, canSkipSession }).disabled}
            style={{ background: PRIMARY.BASE, border: `1px solid ${PRIMARY.BASE}` }}
          />
        )}
      </div>
    </div>
  );
};

const BCBADashboard = () => {
  const dispatch = useDispatch<DispatchProperties>();
  const navigate = useNavigate();

  const loading = useSelector((state) => state.dashboard.loading);
  const bcbaDashboard = useSelector((state) => state.dashboard.bcbaDashboard);
  const error = useSelector((state) => state.dashboard.error);
  const [showPendingApproval, setShowPendingApproval] = useState(false);

  useEffect(() => {
    dispatch(getBcbaDashboard());
  }, [dispatch]);

  if (loading) return <Loader />;

  if (error?.status && !bcbaDashboard) {
    return (
      <div className="btEmptyState">
        <Text title="Unable to load dashboard" type="h6" font="header" fontWeight="semiBold" />
        <Text title={error.title || 'Something went wrong. Please try again later.'} type="psm" />
        <CustomButton
          title="Retry"
          sizeType="sm"
          onClick={() => dispatch(getBcbaDashboard())}
          style={{ background: PRIMARY.BASE, border: `1px solid ${PRIMARY.BASE}` }}
        />
      </div>
    );
  }

  const clients = bcbaDashboard?.clients ?? [];

  return (
    <div className="btDashboardContainer">
      <BcbaSummaryCards
        summary={bcbaDashboard?.summary ?? null}
        onPendingSignOffClick={() => setShowPendingApproval((prev) => !prev)}
      />
      <PendingApproval 
        isOpen={showPendingApproval} 
        onClose={() => setShowPendingApproval(false)} 
        onRefreshDashboard={() => dispatch(getBcbaDashboard())}
      />
      <ReassessmentAlert count={bcbaDashboard?.upcomingReassessmentsCount ?? 0} />

      <div className="btSectionHeader">
        <div className="btSectionHeaderLeft">
          <Calendar color="#313130" />
          <Text className="btSectionTitle" title="My clients" type="h6" font="header" fontWeight="semiBold" />
          <span className="bcbaSectionCount">
            {clients.length} client{clients.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="btSectionHeaderRight">
          <CustomButton
            className="btHeaderLink"
            title="View Full Calendar"
            buttonType="link"
            sizeType="sm"
            onClick={() => navigate('/calendar')}
          />
        </div>
      </div>

      {clients.length === 0 ? (
        <div className="btEmptyState">
          <Text title="No clients assigned yet" type="h6" font="header" fontWeight="semiBold" />
        </div>
      ) : (
        <div className="btSessionsList">
          {clients.map((client) => (
            <BcbaClientCard key={client.client.id} client={client} />
          ))}
        </div>
      )}
    </div>
  );
};

export default BCBADashboard;
